/**
 * SliderRating — 1–10 slider question with large number display.
 *
 * Layout (matching the reference screenshots):
 *  - Big icon at top (caller-supplied)
 *  - Huge "5" number
 *  - Label under the number ("Average", "Gassed quickly", "Never tired")
 *  - Range slider
 *  - End-labels under the slider
 *  - Numbered buttons 1–10 (tap-to-jump)
 *
 * The number strip is important for touch — the slider alone is hard to
 * hit a specific value on mobile.
 */

import type { ReactNode } from 'react';
import './SliderRating.css';

export type SliderRatingProps = {
    value: number;
    onChange: (next: number) => void;
    /** hero icon rendered above the number */
    icon: ReactNode;
    /** label shown under the big number — e.g. "Average", "Gassed quickly" */
    labelForValue?: (value: number) => string;
    /** end-labels under the slider */
    leftLabel: string;
    rightLabel: string;
    min?: number;
    max?: number;
    disabled?: boolean;
};

const DEFAULT_LABELS = ['Very low', 'Low', 'Low-ish', 'Below avg', 'Average', 'Solid', 'Above avg', 'Strong', 'Excellent', 'Elite'];

function SliderRating({
    value,
    onChange,
    icon,
    labelForValue,
    leftLabel,
    rightLabel,
    min = 1,
    max = 10,
    disabled,
}: SliderRatingProps) {
    const clamped = Math.min(Math.max(value, min), max);
    const label = labelForValue ? labelForValue(clamped) : DEFAULT_LABELS[clamped - 1] ?? '';

    return (
        <div className="wizard-slider">
            <div className="wizard-slider__icon" aria-hidden="true">{icon}</div>
            <div className="wizard-slider__value">{clamped}</div>
            <div className="wizard-slider__value-label">{label}</div>

            <input
                type="range"
                min={min}
                max={max}
                step={1}
                value={clamped}
                onChange={(e) => onChange(Number(e.target.value))}
                disabled={disabled}
                className="wizard-slider__range"
                aria-label={`Rating ${clamped} of ${max}`}
            />

            <div className="wizard-slider__ends">
                <span>{leftLabel}</span>
                <span>{rightLabel}</span>
            </div>

            <div className="wizard-slider__numbers" role="radiogroup" aria-label="Rating value">
                {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => {
                    const selected = n === clamped;
                    return (
                        <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={selected ? 'true' : 'false'}
                            className={`wizard-slider__number${selected ? ' is-selected' : ''}`}
                            onClick={() => onChange(n)}
                            disabled={disabled}
                        >
                            {n}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default SliderRating;
